import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { NEW_PERIOD } from "@/constants/testIds";

export default function NewPeriodModal({ open, onClose, onCreated }) {
    const [ppe, setPpe] = useState("");
    const [carry, setCarry] = useState(0);
    const [last, setLast] = useState(null);
    const [saving, setSaving] = useState(false);
    // Track whether the operator has touched the date field. The suggest-next-ppe fetch
    // resolves *after* the modal renders, so without this guard it would silently overwrite
    // whatever the operator has already typed.
    const touched = useRef(false);

    useEffect(() => {
        if (!open) return;
        touched.current = false;
        setPpe("");
        api.get("/pay-periods/suggest-next-ppe").then((r) => {
            if (!touched.current) setPpe(r.data.next_ppe_date);
            setCarry(r.data.carry_over_staff);
            setLast(r.data.last_ppe_date);
        });
    }, [open]);

    if (!open) return null;

    const create = async () => {
        if (!ppe) return;
        setSaving(true);
        try {
            const { data } = await api.post("/pay-periods", { ppe_date: ppe });
            toast.success("Pay period created");
            onCreated?.(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not create period");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div
                data-testid={NEW_PERIOD.modal}
                className="relative bg-white border border-neutral-200 shadow-2xl w-full max-w-[520px] rounded-[3px]"
            >
                <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                            Start a new pay period
                        </div>
                        <div className="font-display font-bold text-2xl text-[color:var(--kp-charcoal)] mt-0.5">
                            Confirm PPE date
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 text-neutral-500 hover:text-black">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-6">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                        Pay Period Ending
                    </div>
                    <input
                        data-testid={NEW_PERIOD.ppeInput}
                        type="date"
                        value={ppe}
                        onChange={(e) => { touched.current = true; setPpe(e.target.value); }}
                        className="kp-focus w-full border border-neutral-300 px-3 py-2.5 text-base bg-white font-mono-money rounded-[3px]"
                    />
                    <p className="text-xs text-neutral-500 mt-2">
                        {last
                            ? `Suggested: 14 days after last PPE (${last}). Must be a Tuesday.`
                            : "No previous period found — suggested next Tuesday. Must be a Tuesday."}
                    </p>

                    <div className="mt-6 border-l-2 pl-3" style={{ borderLeftColor: "var(--kp-red)" }}>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display">
                            Staff carried over
                        </div>
                        <div className="font-display font-bold text-3xl mt-0.5 text-[color:var(--kp-charcoal)]">
                            {carry}
                        </div>
                        <div className="text-xs text-neutral-500 mt-1">
                            All active employees — rates, dependants, tax status, and recurring allowances preserved. Hours start at 0.
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
                    <button
                        data-testid={NEW_PERIOD.cancelBtn}
                        onClick={onClose}
                        className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid={NEW_PERIOD.confirmBtn}
                        onClick={create}
                        disabled={saving || !ppe}
                        className="px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-70"
                        style={{ background: "var(--kp-red)" }}
                    >
                        {saving ? "Creating…" : "Create pay period →"}
                    </button>
                </div>
            </div>
        </div>
    );
}
